import React, { useEffect, useRef, useState } from 'react';
import { withLocalize } from 'react-localize-redux';
import AuthService from './service';
import useSsoDetection from './useSsoDetection';
import useInitLocalize from './useInitLocalize';
import AuthPageShell, { storeShape } from './components/AuthPageShell';
import AuthLayout from './components/AuthLayout';
import LoginForm from './components/LoginForm';
import ForgotForm from './components/ForgotForm';
import SsoForm from './components/SsoForm';
import MfaForm from './components/MfaForm';
import {
    AUTH_ACTION, LOGIN_SUBSTATUS, CANT_SSO_MESSAGE, FREESIGNUP_STEP2_URL, RESET_SENT_URL,
} from './constants';

const initialAction = () =>
    (window.location.hash === '#/sso' || window.location.pathname.endsWith('/sso'))
        ? AUTH_ACTION.SSO
        : AUTH_ACTION.LOGIN;

const LoginApp = withLocalize(function LoginApp({ translate, initialize, addTranslationForLanguage }) {
    useInitLocalize(initialize, addTranslationForLanguage);

    const serviceRef = useRef(null);
    if (!serviceRef.current) serviceRef.current = new AuthService();
    const service = serviceRef.current;

    const [action, setAction] = useState(initialAction);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [mfaToken, setMfaToken] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const pageLoadTime = useRef(new Date().getTime());

    // A same-email SSO result can resolve after leaving LOGIN; ignore it then.
    const actionRef = useRef(action);
    useEffect(() => {
        actionRef.current = action;
    });

    const checkForSSO = useSsoDetection(() => {
        if (actionRef.current !== AUTH_ACTION.LOGIN) return;
        setAction(AUTH_ACTION.SSO);
        setError('');
        setPassword('');
        setMfaToken('');
    });

    const onEmailChange = value => { setEmail(value); checkForSSO(value); };

    const fragment = () => (window.location.hash ? window.location.hash.slice(1) : '');

    const validate = recaptchaResponse => {
        if (action === AUTH_ACTION.MFA) {
            if (!mfaToken) {
                setError('Please enter a 6-digit token');
                return false;
            }
        } else if (action === AUTH_ACTION.SSO) {
            if (!email) {
                setError('Please enter a valid email');
                return false;
            }
        } else if (!email) {
            setError(!password ? 'Please enter a valid email and password' : 'Please enter a valid email');
            return false;
        } else if (!password) {
            setError('Please enter a valid email and password');
            return false;
        } else if (!recaptchaResponse) {
            setError('Please complete the reCAPTCHA verification.');
            return false;
        }
        return true;
    };

    const doLogin = (recaptchaResponse, { sso = false } = {}) => {
        if (!validate(recaptchaResponse)) return;

        setError('');
        setLoading(true);
        service
            .login({ email, password, mfaToken: mfaToken || null, sso, fragment: fragment(), recaptchaResponse })
            .then(data => {
                setLoading(false);
                if (data.status === 0) {
                    window.location.reload();
                } else if (data.status !== 1) {
                    setError(data.value);
                } else if (data.substatus === LOGIN_SUBSTATUS.NEED_MFA) {
                    setAction(AUTH_ACTION.MFA);
                } else if (data.substatus === LOGIN_SUBSTATUS.REDIRECT) {
                    window.location.href = data.value;
                } else if (data.substatus === LOGIN_SUBSTATUS.NEED_SSO) {
                    setAction(AUTH_ACTION.SSO);
                } else if (data.substatus === LOGIN_SUBSTATUS.CANT_SSO) {
                    setAction(AUTH_ACTION.LOGIN);
                    setError(CANT_SSO_MESSAGE);
                } else {
                    setError(data.value);
                }
            })
            .catch(() => {
                setLoading(false);
                setError(translate('STATIC.PAGES.LOGIN.GENERIC_ERROR'));
            });
    };

    const doReset = recaptchaResponse => {
        setError('');
        setLoading(true);
        service
            .resetPassword({ email, recaptchaResponse })
            .then(data => {
                setLoading(false);
                if (data.status === 0) window.location = data.value || RESET_SENT_URL;
                else setError(data.value);
            })
            .catch(() => {
                setLoading(false);
                setError(translate('STATIC.PAGES.LOGIN.GENERIC_ERROR'));
            });
    };

    const onGoogleSuccess = accessToken => {
        setError('');
        setLoading(true);
        service.loginWithGoogle(accessToken).then(data => {
            const { success, message, user_exists: userExists, proceed } = data;
            if (success && !userExists) {
                setLoading(false);
                const expires = new Date(new Date().getTime() + 10000).toUTCString();
                document.cookie = `oauth-access-token=${accessToken}; expires=${expires}`;
                window.location.href = FREESIGNUP_STEP2_URL;
            } else if (success && proceed) {
                setLoading(false);
                window.location.reload();
            } else {
                setLoading(false);
                setError(translate(`STATIC.PAGES.SIGNUP.${message}`));
            }
        }).catch(() => {
            setLoading(false);
            setError(translate('STATIC.PAGES.LOGIN.GENERIC_ERROR'));
        });
    };

    const backToLogin = () => {
        setAction(AUTH_ACTION.LOGIN);
        setError('');
        setPassword('');
        setMfaToken('');
    };

    if (action === AUTH_ACTION.FORGOT) {
        return (
            <AuthLayout heading="STATIC.PAGES.LOGIN.RESET_HEADING">
                <ForgotForm email={email} error={error} loading={loading} onEmailChange={setEmail} onBack={backToLogin} onSubmit={doReset} />
            </AuthLayout>
        );
    }
    if (action === AUTH_ACTION.SSO) {
        return (
            <AuthLayout heading="STATIC.PAGES.LOGIN.WELCOME">
                <SsoForm email={email} error={error} loading={loading} onEmailChange={onEmailChange} onBack={backToLogin} onSubmit={() => doLogin(null, { sso: true })} />
            </AuthLayout>
        );
    }
    if (action === AUTH_ACTION.MFA) {
        return (
            <AuthLayout heading="STATIC.PAGES.LOGIN.WELCOME">
                <MfaForm mfaToken={mfaToken} error={error} loading={loading} onTokenChange={setMfaToken} onBack={backToLogin} onSubmit={() => doLogin(null)} />
            </AuthLayout>
        );
    }
    return (
        <AuthLayout heading="STATIC.PAGES.LOGIN.WELCOME">
            <LoginForm
                email={email} password={password} error={error} loading={loading}
                onEmailChange={onEmailChange} onPasswordChange={setPassword}
                onForgot={() => { setAction(AUTH_ACTION.FORGOT); setError(''); }}
                onSso={() => { setAction(AUTH_ACTION.SSO); setError(''); }}
                onSubmit={token => doLogin(token)}
                onGoogleSuccess={onGoogleSuccess}
                onGoogleUnavailable={() => setError(translate('STATIC.PAGES.LOGIN.GENERIC_ERROR'))}
                onRecaptchaError={() => setError(translate('STATIC.PAGES.LOGIN.GENERIC_ERROR'))}
                googleTrackingParams={{ time_to_submit: (new Date().getTime() - pageLoadTime.current) / 1000, origin: 'login_page' }}
            />
        </AuthLayout>
    );
});

export default function LoginPage({ store }) {
    return (
        <AuthPageShell store={store}>
            <LoginApp />
        </AuthPageShell>
    );
}

LoginPage.propTypes = {
    store: storeShape.isRequired,
};
